import { useMemo } from "react"

import classNames from "classnames"

/**
 * Hook to merge a base className with a user-provided className.
 * Supports both static string classNames and function-based classNames that receive state.
 *
 * @param baseClassName - The base className(s) to always apply
 * @param userClassName - Optional user className, can be a string or function receiving state
 * @returns A merged className string, or a function that returns a merged className string
 *
 * @example
 * ```typescript
 * // Static className
 * const className = useMergeClassName("btn", "btn-primary")
 * // Result: "btn btn-primary"
 *
 * // Function-based className with state
 * const getClassName = useMergeClassName<{ open: boolean }>(
 *   "dropdown",
 *   (state) => state.open ? "dropdown--open" : undefined
 * )
 * const className = getClassName({ open: true })
 * // Result: "dropdown dropdown--open"
 * ```
 */
export function useBaseUiClassName<TState = never>(
  baseClassName: string,
  userClassName?: string | ((state: TState) => string | undefined)
) {
  return useMemo(() => {
    return typeof userClassName === "function"
      ? (state: TState) => classNames(baseClassName, userClassName(state))
      : classNames(baseClassName, userClassName)
  }, [baseClassName, userClassName])
}
